import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './ServicesPage.css';
import { PageHero } from '../../components/PageHero/PageHero';
import freightImg from '../../images/offers-freight.jpg';
import warehouseImg from '../../images/offers-warehouse.jpg';
import deliveryImg from '../../images/offers-delivery.jpg';
import planImg from '../../images/offers-plan.jpg';
import heroImg from '../../images/hero_warehouse.jpg';

const SERVICES = [
  {
    image: freightImg,
    tag: 'Most Requested',
    title: 'Freight Transportation',
    href: '/services/freight-transportation',
    outcome: 'Reliable FTL & LTL across the Southeast',
    bullets: [
      'Full truckload & LTL freight',
      'Temp-controlled capacity available',
      'Real-time tracking & proactive alerts',
      'Vetted, compliant carrier network',
    ],
  },
  {
    image: warehouseImg,
    tag: '',
    title: 'Warehouse & Storage',
    href: '/services/warehouse-storage',
    outcome: 'Flexible storage in Tampa Bay metro',
    bullets: [
      'Short & long-term storage options',
      'Climate-controlled facility',
      'Inventory management & reporting',
      'Pick, pack & kitting services',
    ],
  },
  {
    image: deliveryImg,
    tag: '',
    title: 'Last-Mile Delivery',
    href: '/services/last-mile-delivery',
    outcome: 'Consistent execution on the final leg',
    bullets: [
      'Residential & commercial delivery',
      'POD capture & real-time status',
      'White-label delivery available',
      'Schedule-based & on-demand routing',
    ],
  },
  {
    image: planImg,
    tag: '',
    title: 'Custom Logistics Plan',
    href: '/services/custom-logistics',
    outcome: 'Built around your supply chain, not ours',
    bullets: [
      'Full supply chain audit',
      'Dedicated account management',
      'Carrier + warehouse integration',
      'Ongoing optimization & reporting',
    ],
  },
];

export const ServicesPage = () => {
  const cardsRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const obs = new IntersectionObserver(
      entries => entries.forEach(e => {
        if (e.isIntersecting) { e.target.classList.add('is-visible'); obs.unobserve(e.target); }
      }),
      { threshold: 0.06 }
    );
    cardsRef.current?.querySelectorAll<HTMLElement>('.rn-reveal').forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  return (
    <>
      <Helmet>
        <title>Logistics Services — Renuit Logistics</title>
        <meta name="description" content="FTL, LTL freight brokerage, warehousing &amp; storage, last-mile delivery, and custom 3PL plans for Southeast US and Gulf Coast shippers." />
        <link rel="canonical" href="https://www.renuit.pro/services" />
        <meta property="og:title" content="Logistics Services — Renuit Logistics" />
        <meta property="og:description" content="Freight transportation, warehousing, last-mile delivery, and custom logistics plans for Southeast US shippers." />
        <meta property="og:url" content="https://www.renuit.pro/services" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.renuit.pro/og-image.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@RenuitLogistics" />
        <meta name="twitter:title" content="Logistics Services — Renuit Logistics" />
        <meta name="twitter:description" content="Freight transportation, warehousing, last-mile delivery, and custom logistics plans for Southeast US shippers." />
        <meta name="twitter:image" content="https://www.renuit.pro/og-image.jpg" />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'ItemList',
          name: 'Renuit Logistics Services',
          url: 'https://www.renuit.pro/services',
          numberOfItems: 4,
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              item: {
                '@type': 'Service',
                name: 'Freight Transportation',
                url: 'https://www.renuit.pro/services/freight-transportation',
              },
            },
            {
              '@type': 'ListItem',
              position: 2,
              item: {
                '@type': 'Service',
                name: 'Warehouse & Storage',
                url: 'https://www.renuit.pro/services/warehouse-storage',
              },
            },
            {
              '@type': 'ListItem',
              position: 3,
              item: {
                '@type': 'Service',
                name: 'Last-Mile Delivery',
                url: 'https://www.renuit.pro/services/last-mile-delivery',
              },
            },
            {
              '@type': 'ListItem',
              position: 4,
              item: {
                '@type': 'Service',
                name: 'Custom Logistics Plan',
                url: 'https://www.renuit.pro/services/custom-logistics',
              },
            },
          ],
        })}</script>
      </Helmet>

      <PageHero
        image={heroImg}
        tag="What We Do"
        heading="End-to-End Logistics Built for the Southeast"
        subheading="Every service is backed by a named account manager, vetted carriers, and transparent tracking — not a call center."
      />

      <section className="rn-services" ref={cardsRef}>
        <div className="rn-services__inner">

          <div className="rn-services__grid">
            {SERVICES.map(({ image, tag, title, href, outcome, bullets }, i) => (
              <article
                key={href}
                className={`rn-svc-card rn-reveal rn-reveal-delay-${(i % 2) + 1}`}
              >
                <div className="rn-svc-card__img-wrap">
                  <img
                    src={image}
                    alt={title}
                    className="rn-svc-card__img"
                    loading="lazy"
                    decoding="async"
                  />
                  {tag && <span className="rn-svc-card__tag">{tag}</span>}
                </div>
                <div className="rn-svc-card__body">
                  <h2 className="rn-svc-card__title">{title}</h2>
                  <p className="rn-svc-card__outcome">{outcome}</p>
                  <ul className="rn-svc-card__bullets">
                    {bullets.map(b => (
                      <li key={b} className="rn-svc-card__bullet">{b}</li>
                    ))}
                  </ul>
                  <Link to={href} className="rn-svc-card__link">
                    Learn more <span aria-hidden="true">→</span>
                  </Link>
                </div>
              </article>
            ))}
          </div>

          <div className="rn-services__cta rn-reveal">
            <p className="rn-services__cta-text">Not sure which service fits your operation?</p>
            <Link to="/quote?type=consultation" className="cs-button-solid">
              Talk to an Advisor
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};
